/**
 * FAT #19 Phase 3 — Subscription/plan context.
 *
 * Single source of truth for the frontend:
 *   /api/subscription/me  → per-user capability set + status
 *   /api/plans            → static plan catalog + all_capabilities
 *
 * Consumers:
 *   • <CapabilityGate>           — locks CTAs the user can't call
 *   • <UpgradePage>              — feature-matrix visualization
 *   • Dashboard tile locks       — (Phase 3b)
 *   • 402 axios interceptor      — router redirect on backstop failures
 *
 * NEVER hardcode plan names in UI logic — everything derives from
 * hasCapability() or the plans catalog.
 */
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { apiGet } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const SubscriptionContext = createContext(null);

export function SubscriptionProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [me, setMe] = useState(null);
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const [mePayload, plansPayload] = await Promise.all([
        apiGet("/subscription/me"),
        apiGet("/plans").catch(() => null), // public, unlikely to fail
      ]);
      setMe(mePayload);
      if (plansPayload) setPlans(plansPayload);
    } catch {
      /* soft-fail; interceptor will handle real 402s */
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => { refresh(); }, [refresh]);

  // FAT #19 Phase 3 fix — refetch subscription snapshot on route change and
  // when the tab regains focus. Without this, admin-side subscription changes
  // (e.g., bulk Beta restores) don't reflect for users with an already-open
  // session until they hard-reload. `/api/subscription/me` is a cheap single
  // Mongo find; the extra call cost is negligible.
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => { refresh(); }, [refresh, location.pathname]);
  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  // FAT #19 Phase 3 — Global 402 backstop. api.js dispatches `wc:capability-gate`
  // on any capability-gate 402 that slips past a <CapabilityGate>. We route to
  // /upgrade with the same query-param contract the gate uses.
  useEffect(() => {
    const handler = (e) => {
      const d = e?.detail || {};
      if (location.pathname.startsWith("/upgrade")) return;
      const q = new URLSearchParams({
        code: d.code || "",
        capability: d.capability || "",
        from: location.pathname + location.search,
      });
      navigate(`${d.upgrade_url || "/upgrade"}?${q.toString()}`);
    };
    window.addEventListener("wc:capability-gate", handler);
    return () => window.removeEventListener("wc:capability-gate", handler);
  }, [navigate, location]);

  const value = useMemo(() => {
    const capsArr = me?.capabilities || [];
    const capsSet = new Set(capsArr);
    return {
      loading,
      raw: me,
      plans,
      plan: me?.plan || "Beta",
      subscriptionStatus: me?.subscription_status || "active",
      isActive: me?.is_active ?? true,
      daysRemaining: me?.days_remaining ?? null,
      capabilities: capsArr,
      notIncluded: me?.not_included || [],
      betaEndDate: me?.beta_end_date || null,
      subscriptionEndDate: me?.subscription_end_date || null,
      isAdmin: !!me?.is_admin,
      hasCapability: (key) => capsSet.has(key),
      refresh,
    };
  }, [me, plans, loading, refresh]);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error("useSubscription must be used inside <SubscriptionProvider>");
  return ctx;
}
